<template>
    <div>
        <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="opened"
                class="fixed left-0 top-[56px] z-498 h-full w-full bg-gray-800/30 backdrop-blur-[2px] backdrop-filter md:hidden"
                @click="$emit('close')"
            ></div>
        </transition>

        <transition
            enter-active-class="transition duration-500 ease-out delay-100"
            enter-from-class="transform -translate-x-40 opacity-0"
            enter-to-class="transform translate-x-0 opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="transform opacity-100"
            leave-to-class="transform -translate-x-40 opacity-0"
        >
            <div
                v-if="opened"
                data-cy="side-menu-mobile"
                class="fixed left-0 top-0 z-499 mt-14 flex h-full min-h-screen w-full max-w-[600px] shrink-0 flex-col bg-gray-100 pb-14 md:hidden"
            >
                <div class="flex flex-col overflow-y-scroll grow">
                    <nav
                        class="border-r border-gray-200"
                        aria-label="Sidebar"
                        role="navigation"
                    >
                        <div
                            class="px-2 py-5 bg-white border-b border-gray-200"
                        >
                            <inertia-link
                                v-for="link in links"
                                :key="link.href"
                                :href="link.href"
                                :data-testid="link.id"
                                :aria-current="
                                    page.startsWith(link.id) ? 'page' : null
                                "
                                :class="{
                                    'bg-gray-200 text-gray-600':
                                        page.startsWith(link.id),
                                    'text-gray-500': !page.startsWith(link.id),
                                }"
                                class="flex items-center justify-between text-sm font-medium transition duration-200 bg-white rounded-[6px] group hover:bg-gray-200 hover:text-gray-600 xl:w-auto"
                                @click="$emit('close')"
                            >
                                <div class="flex items-center py-4 pl-3">
                                    <component
                                        :is="link.icon"
                                        class="w-6 h-6 mr-3 transition duration-200 group-hover:text-gray-600"
                                        :class="{
                                            'text-gray-400': !page.startsWith(
                                                link.id
                                            ),
                                        }"
                                    ></component>

                                    <p>
                                        {{ link.name }}
                                    </p>
                                </div>
                                <div
                                    v-if="link.count != null"
                                    class="mr-4 flex h-5 w-[37px] items-center justify-center rounded-2lg bg-gray-100 text-xs font-normal text-gray-500"
                                >
                                    {{ link.count }}
                                </div>
                            </inertia-link>
                        </div>

                        <div
                            class="bg-gray-100 px-2 pb-[23px]"
                            @click="$emit('close')"
                        >
                            <inertia-link
                                v-if="globalProps.isAdmin && !isInKiosk"
                                :href="route('kiosk.users.index')"
                                data-cy="side-menu-mobile-kiosk-link"
                            >
                                <div
                                    class="py-4 pl-5 text-base font-medium text-gray-500 border-b border-gray-200"
                                >
                                    Kiosk
                                </div>
                            </inertia-link>

                            <inertia-link
                                v-if="globalProps.isAdmin && isInKiosk"
                                :href="route('home')"
                            >
                                <div
                                    class="py-4 pl-5 text-base font-medium text-gray-500 border-b border-gray-200"
                                >
                                    Back to App
                                </div>
                            </inertia-link>

                            <inertia-link
                                href="/logout"
                                method="POST"
                                as="button"
                            >
                                <div
                                    class="py-4 pl-5 text-base font-medium text-gray-500 border-b border-gray-200"
                                >
                                    Logout
                                </div>
                            </inertia-link>
                            <a
                                href="https://docs.topol.io/"
                                target="_blank"
                            >
                                <div
                                    class="py-4 pl-5 mt-10 text-base font-medium text-gray-500 border-b border-gray-200"
                                >
                                    Plugin Documentation
                                </div>
                            </a>
                        </div>
                    </nav>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import { route } from 'ziggy-js';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import sidebar from '@/data/sidebar';
import pluginSidebar from '@/data/pluginSidebar';

defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
});

defineEmits(['close']);

const { links } = pluginSidebar();

const page = computed(() => usePage<ITopolPageProps>().url);

const isInKiosk = computed(() => {
    return usePage<IRhizomPageProps>().url.startsWith('/kiosk');
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
</script>

<style></style>
